// Locale-aware formatting. The active number/date locale is set globally by
// PrefsContext (setFormatLocale) and can differ from the UI language.
let LOCALE = "en-US";
export const setFormatLocale = (loc) => { if (loc) LOCALE = loc; };
export const getFormatLocale = () => LOCALE;

// Crypto can be worth a small fraction of a cent per unit, where the usual 2-4
// decimal places just show 0. Pass `{ crypto: true }` (based on the instrument's
// asset_type) to any of usd/money/num below to show up to 9 decimals instead —
// Intl only uses as many as the value actually needs, so normal prices are
// unaffected either way.
export const isCryptoType = (assetType) => !!assetType && String(assetType).toUpperCase().includes("CRYPTO");
const CRYPTO_MAX_DECIMALS = 9;

export const usd = (v, opts = {}) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: opts.min ?? 2,
    maximumFractionDigits: opts.crypto ? CRYPTO_MAX_DECIMALS : (opts.max ?? 2),
  }).format(v);
};

// Headline prices: cents only when there are cents. "€16" reads as a price, "€16.00"
// reads as a line item, and the pricing cards are full of the former. Everything that
// quotes a subscription price goes through here so €16 and €16.50 both come out right.
export const price = (v, ccy = "EUR") => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  try {
    return new Intl.NumberFormat(LOCALE, {
      style: "currency",
      currency: ccy,
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(v);
  } catch {
    return `${v} ${ccy}`;
  }
};

export const money = (v, ccy = "USD", opts = {}) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  try {
    const intlOpts = { style: "currency", currency: ccy };
    if (opts.crypto) {
      intlOpts.minimumFractionDigits = 2;
      intlOpts.maximumFractionDigits = CRYPTO_MAX_DECIMALS;
    }
    return new Intl.NumberFormat(LOCALE, intlOpts).format(v);
  } catch {
    return `${v.toFixed(2)} ${ccy}`;
  }
};

export const pct = (v) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  const s = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(v));
  return `${v >= 0 ? "+" : "−"}${s}%`;
};

export const num = (v, d = 2, opts = {}) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: opts.crypto ? CRYPTO_MAX_DECIMALS : d }).format(v);
};

export const shortDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(LOCALE, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return s;
  }
};

// Compact date for chart axis ticks (no year) — keeps labels short so more of
// them fit on narrow screens without crowding or truncation.
export const axisDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(LOCALE, { month: "short", day: "numeric" });
  } catch {
    return s;
  }
};

// Intraday chart labels, for when a chart point is a candle (5, 15 or 60 minutes)
// rather than a day. `axisTime` is the tick on a one-day chart ("14:35" / "2:35 PM");
// `dateTime` is the tooltip line ("Oct 2, 14:35"). Both read the browser's time zone,
// like every other date here, so a US stock trades 15:30-22:00 for a Berlin reader.
export const axisTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleTimeString(LOCALE, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return s;
  }
};

export const dateTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString(LOCALE, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return s;
  }
};

export const gainColor = (v) => (v > 0 ? "text-emerald-600" : v < 0 ? "text-rose-600" : "text-slate-500");
